import { useCallback, useEffect, useRef, useState } from 'react';
import { createSummaryMetadata } from '@/components/summarizer/summary-metadata';
import { DEFAULT_SUMMARY_LENGTH_INDEX, SUMMARY_LENGTH_OPTIONS } from '@/lib/link-preview/summary-options';
import { toTrimmedNonEmptyString } from '@/lib/utils/type-guards';
import type { StreamState, SummarizeOverrides, SummaryContextSnapshot } from '../types';
import type { SummaryBaseState } from './useSummarySettings';
import { useTweetStreaming } from './useTweetStreaming';
export interface UseSummarizeRequestParameters {
  buildPrompt: (custom: string, includeImages: boolean) => string;
  includeImagesInPrompt: boolean;
  setSummaryContextSnapshot: (snapshot: SummaryContextSnapshot | null) => void;
  settings: SummaryBaseState;
  summaryImageCount: number;
  tweetId: string;
}
export interface UseSummarizeRequestResult {
  hasSummary: boolean;
  isSummarizing: boolean;
  summarize: (overrides?: SummarizeOverrides) => Promise<void>;
  summaryState: StreamState;
}
export function useSummarizeRequest({
  tweetId,
  settings,
  includeImagesInPrompt,
  summaryImageCount,
  buildPrompt,
  setSummaryContextSnapshot
}: UseSummarizeRequestParameters): UseSummarizeRequestResult {
  const [summaryState, setSummaryState] = useState<StreamState>(() => ({
    status: 'idle',
    text: '',
    error: null,
    metadata: createSummaryMetadata({
      availableImages: summaryImageCount,
      shouldIncludeImages: includeImagesInPrompt,
      mode: settings.summaryMode
    })
  }));
  const summarizeInFlightReference = useRef(false);
  const summarizationAbortReference = useRef<AbortController | null>(null);
  const isMountedReference = useRef(true);
  const startTweetStream = useTweetStreaming(isMountedReference);
  useEffect(() => {
    isMountedReference.current = true;
    const abortReference = summarizationAbortReference;
    return () => {
      isMountedReference.current = false;
      abortReference.current?.abort();
    };
  }, []);
  const summarize = useCallback(async (overrides?: SummarizeOverrides) => {
    if (summarizeInFlightReference.current) {
      return;
    }
    summarizeInFlightReference.current = true;
    const nextSummaryLengthIndex = overrides?.summaryLengthIndex ?? settings.summaryLengthIndex;
    const summaryLengthValue = SUMMARY_LENGTH_OPTIONS[nextSummaryLengthIndex]?.value ?? SUMMARY_LENGTH_OPTIONS[DEFAULT_SUMMARY_LENGTH_INDEX].value;
    const nextModel = overrides?.model ?? settings.summaryModel;
    const nextCustomPrompt = overrides?.customPrompt ?? settings.customPrompt;
    const nextSearchEnabled = overrides?.summarySearchEnabled ?? settings.summarySearchEnabled;
    const trimmedPrompt = toTrimmedNonEmptyString(nextCustomPrompt) ?? '';
    const prompt = buildPrompt(trimmedPrompt, includeImagesInPrompt);
    const snapshot: SummaryContextSnapshot = {
      prompt,
      includeImages: includeImagesInPrompt,
      customPrompt: trimmedPrompt.length > 0 ? trimmedPrompt : null,
      summaryLength: summaryLengthValue,
      model: nextModel,
      searchMode: nextSearchEnabled ? 'auto' : 'off'
    };
    setSummaryContextSnapshot(snapshot);
    const requestBody = {
      summaryLength: summaryLengthValue,
      model: nextModel,
      includeImages: includeImagesInPrompt,
      mode: 'summary' as const,
      searchMode: nextSearchEnabled ? 'auto' : 'off',
      ...(trimmedPrompt.length > 0 ? {
        customPrompt: trimmedPrompt
      } : {})
    };
    const initialMetadata = createSummaryMetadata({
      summaryLength: summaryLengthValue,
      requestedModel: nextModel,
      customPrompt: trimmedPrompt.length > 0 ? trimmedPrompt : null,
      mode: 'summary',
      searchMode: nextSearchEnabled ? 'auto' : 'off',
      requestedSearchMode: nextSearchEnabled ? 'auto' : 'off',
      shouldIncludeImages: includeImagesInPrompt,
      availableImages: summaryImageCount
    });
    try {
      await startTweetStream({
        endpoint: `/api/tweets/${tweetId}/summarize`,
        requestBody,
        abortRef: summarizationAbortReference,
        setState: setSummaryState,
        errorMessage: 'Unable to summarize tweet.',
        initialMetadata
      });
    } finally {
      summarizeInFlightReference.current = false;
    }
  }, [buildPrompt, includeImagesInPrompt, summaryImageCount, setSummaryContextSnapshot, settings.customPrompt, settings.summaryLengthIndex, settings.summaryModel, settings.summarySearchEnabled, startTweetStream, tweetId]);
  return {
    summaryState,
    isSummarizing: summaryState.status === 'loading' || summaryState.status === 'streaming',
    hasSummary: Boolean(toTrimmedNonEmptyString(summaryState.text)),
    summarize
  };
}